import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigurationService } from 'src/app/core/config/configuration.service';
import { HttpParamsFromListRequestDTO, ListRequestDTO, ListResponseDTO } from '..';

@Injectable()
export class InvoiceProxy {
    constructor(
        private http: HttpClient,
        private configurationService: ConfigurationService,
    ) {}

    async get(
        options: ListRequestDTO & {
            practiceId?: string;
            isArchived?: boolean;
            isDraft?: boolean;
            isProcessed?: boolean;
            isSent?: boolean;
            isDiscarded?: boolean;
        },
    ): Promise<ListResponseDTO<InvoiceSummaryDTO>> {
        const url = this.configurationService.APIURL + '/api/invoices';
        let params = HttpParamsFromListRequestDTO(options);
        if (options.practiceId) {
            params = params.append('practiceId', String(options.practiceId));
        }
        if (options.isArchived) {
            params = params.append('isArchived', String(options.isArchived));
        }
        if (options.isDraft) {
            params = params.append('isDraft', String(options.isDraft));
        }
        if (options.isProcessed) {
            params = params.append('isProcessed', String(options.isProcessed));
        }
        if (options.isSent) {
            params = params.append('isSent', String(options.isSent));
        }
        if (options.isDiscarded) {
            params = params.append('isDiscarded', String(options.isDiscarded));
        }
        return await this.http.get<ListResponseDTO<InvoiceSummaryDTO>>(url, { params: params }).toPromise();
    }

    async getDetails(practiceId: string, invoiceId: string): Promise<InvoiceDetailsDTO> {
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        const url = this.configurationService.APIURL + '/api/invoices/' + invoiceId;
        return await this.http.get<InvoiceDetailsDTO>(url, { params: params }).toPromise();
    }

    async getIntegrationDetails(practiceId: string, invoiceId: string): Promise<InvoiceIntegrationDetailDTO> {
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        const url = this.configurationService.APIURL + '/api/invoices/' + invoiceId + '/integration';
        return await this.http.get<InvoiceIntegrationDetailDTO>(url, { params: params }).toPromise();
    }

    create(practiceId: string, formData: FormData): Observable<any> {
        const url = this.configurationService.APIURL + '/api/invoices';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return this.http.post(url, formData, {
            reportProgress: true,
            observe: 'events',
            params: params,
        });
    }

    async deleteInvoice(practiceId: string, invoiceId: string): Promise<any> {
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        const url = this.configurationService.APIURL + '/api/invoices/' + invoiceId;
        return await this.http.delete(url, { params: params }).toPromise();
    }

    async invoiceNumberExists(invoiceNumber: string): Promise<boolean> {
        const url = this.configurationService.APIURL + '/api/invoices/any';
        let params = new HttpParams();
        params = params.append('invoiceNumber', invoiceNumber);
        return await this.http.get<boolean>(url, { params: params }).toPromise();
    }

    async downloadFile(practiceId: string, invoiceId: string): Promise<Blob> {
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        const url = this.configurationService.APIURL + '/api/invoices/' + invoiceId + '/report';
        return await this.http.get(url, { responseType: 'blob', params: params }).toPromise();
    }

    async archive(practiceId: string, invoiceId: string): Promise<object> {
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        const url = this.configurationService.APIURL + '/api/invoices/' + invoiceId + '/archive';
        return await this.http.put(url, { archive: true }, { params: params }).toPromise();
    }

    async unArchive(practiceId: string, invoiceId: string): Promise<object> {
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        const url = this.configurationService.APIURL + '/api/invoices/' + invoiceId + '/archive';
        return await this.http.put(url, { archive: false }, { params: params }).toPromise();
    }

    async process(practiceId: string, invoiceId: string): Promise<object> {
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        const url = this.configurationService.APIURL + '/api/invoices/' + invoiceId + '/process';
        return await this.http.post(url, {}, { params: params }).toPromise();
    }

    async discard(practiceId: string, invoiceId: string, discardMessage: string): Promise<object> {
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        const url = this.configurationService.APIURL + '/api/invoices/' + invoiceId + '/discard';
        return await this.http.post(url, { discardMessage: discardMessage }, { params: params }).toPromise();
    }

    async send(practiceId: string, invoiceId: string, message: string): Promise<object> {
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        const url = this.configurationService.APIURL + '/api/invoices/' + invoiceId + '/send';
        return await this.http.post(url, { message: message }, { params: params }).toPromise();
    }
}

export interface InvoiceSummaryDTO {
    id: string;
    practiceId: string;
    practiceName: string;
    status: InvoiceStatus;

    number: string;
    fileName: string;
    isRead: boolean;
    isArchived: boolean;
    message: string;

    externalReference: string;
    externalUrl: string;
    externalIntegrationError: string;

    initiallyInvoicedPracticeId: string;
    initiallyInvoicedPracticeName: string;

    draftedOn: Date | string;
    draftedBy: string;
    toProcessOn: Date | string;
    processedOn: Date | string;
    processedBy: string;
    sentOn: string | null;
    sentBy: string;
    discardedOn: string | null;
    discardedBy: string;
}

export enum InvoiceStatus {
    Draft = 0,
    Processed = 1,
    Sent = 2,
    Discarded = 3,
}

export interface InvoiceDetailsDTO {
    id: string;
    practiceId: string;
    practiceName: string;
    isRead: boolean;
    isArchived: boolean;
    number: string;
    fileName: string;
    message: string;
    initiallyInvoicedPracticeId: string;
    initiallyInvoicedPracticeName: string;
    status: InvoiceStatus;

    draftedOn: string | null;
    draftedBy: string;
    toProcessOn: string | null;
    processedOn: string | null;
    processedBy: string;
    sentOn: string | null;
    sentBy: string;
    discardedOn: string | null;
    discardedBy: string;

    externalReference: string;
    externalUrl: string;

    lines: InvoiceLineDTO[];
    receiverEmailAddresses: string[];
}

export interface InvoiceLineDTO {
    id: string;
    test: TestDTO;
    labNumbers: string[];
}

export interface TestDTO {
    reference: string;
    name: string;
    primaryColorHex: string;
    secondaryColorHex: string;
}

export interface InvoiceIntegrationDetailDTO {
    invoiceTo: string;
    journal: string;
    status: string;

    amountFC: number;
    amountDC: number;

    vatAmountFC: number;
    vatAmountDC: number;

    currencyFC: string;
    currencyDC: string;

    externalUrl: string;

    lines: InvoiceIntegrationLineDTO[];
}

export interface InvoiceIntegrationLineDTO {
    amountFC: number;
    amountDC: number;

    vatAmountFC: number;
    vatAmountDC: number;
    vatPercentage: number;
    vatCode: string;

    currencyFC: string;
    currencyDC: string;

    test: TestDTO;
    description: string;
    quantity: number;
    unitPrice: number;
    discount: number;
}
